import { useCallback, useRef, useState } from "react";
import _ from "lodash";

import {
  AddIcon,
  ContentCopyOutlinedIcon,
  DeleteOutlineOutlinedIcon,
} from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import { copyTextToClipboard, isNotUndefined } from "utils/common";
import { format, FORMATS, parseISO } from "utils/datetime";
import { handleInvalidRequest } from "utils/sdk";

import Button from "components/Button";
import IconButton from "components/IconButton";
import Stack from "components/Stack";
import Text from "components/Text";
import TextField from "components/TextField";
import toast, { DEFAULT_ERROR_MESSAGE } from "components/Toast";
import Tooltip from "components/Tooltip";
import { useConfirmationDialog } from "components/useConfirmationDialog";

import {
  IOpenAICustomPromptResponse,
  openAICustomPromptResponseDelete,
  openAIPromptCustomResponseCreateOrUpdate,
} from "entities/AISummaries/sdk";

export interface ICustomResponse {
  initialCustomResponse: IOpenAICustomPromptResponse | undefined;
  openAIDocumentAssistantId: number;
  promptId: number;
  onUpdated: () => void;
}

const CustomResponse = ({
  initialCustomResponse,
  openAIDocumentAssistantId,
  promptId,
  onUpdated,
}: ICustomResponse) => {
  const inputRef = useRef<HTMLInputElement>();

  const { show: showConfirmationDialog } = useConfirmationDialog();

  const [isEditable, setIsEditable] = useState(
    !_.isUndefined(initialCustomResponse)
  );
  const [customResponse, setCustomResponse] = useState(
    initialCustomResponse?.response || ""
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setCustomResponse(e.target.value),
    [setCustomResponse]
  );

  const handleSubmitCustomResponse = useCallback(() => {
    openAIPromptCustomResponseCreateOrUpdate({
      openAIDocumentAssistantId,
      promptId,
      response: customResponse,
    })
      .then(() => {
        toast.successMessage("Your custom answer has been saved.");
        onUpdated();
      })
      .catch(handleInvalidRequest);
  }, [openAIDocumentAssistantId, promptId, customResponse, onUpdated]);

  const handleDelete = useCallback(() => {
    if (!_.isNil(initialCustomResponse)) {
      showConfirmationDialog({
        onConfirm: () =>
          openAICustomPromptResponseDelete({
            openAIDocumentAssistantId,
            customPromptResponseId: initialCustomResponse.id,
          })
            .then(() => {
              if (!_.isUndefined(inputRef.current)) {
                inputRef.current.value = "";
              }

              setIsEditable(false);
              setCustomResponse("");
              onUpdated();
              toast.successMessage(
                "The custom answer has been deleted successfully."
              );
            })
            .catch((error) => {
              const errorMessage =
                error?.message || error?.detail || DEFAULT_ERROR_MESSAGE;

              toast.errorMessage(errorMessage);
            }),
        confirmButtonText: "Delete",
        message: "Are you sure you want to delete the custom answer?",
      });
    }
  }, [
    initialCustomResponse,
    openAIDocumentAssistantId,
    onUpdated,
    showConfirmationDialog,
  ]);

  const handleCopyToClipboard = useCallback(() => {
    copyTextToClipboard({ text: customResponse });
    toast.successMessage("The text has been copied to the clipboard.");
  }, [customResponse]);

  if (!isEditable) {
    return (
      <Button
        variant="outlined"
        color="secondary"
        startIcon={<AddIcon />}
        onClick={() => setIsEditable(true)}
        style={{ alignSelf: "flex-start", marginBottom: theme.spacing(2) }}
      >
        Create my own answer
      </Button>
    );
  }

  return (
    <Stack spacing={1} paddingTop={3} paddingBottom={2}>
      <Text variant="label" color={colors.gray60} fontWeight={600}>
        My edited answer
      </Text>
      <TextField
        inputRef={inputRef}
        multiline
        rows={8}
        placeholder="Write your own answer here..."
        defaultValue={customResponse}
        onChange={_.debounce(handleChange, 200)}
      />
      <Stack
        spacing={1}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <div>
          {isNotUndefined(initialCustomResponse) && (
            <Text variant="text2" sx={{ color: colors.gray60 }}>
              Last edited by {initialCustomResponse.updated_by.first_name}{" "}
              {initialCustomResponse.updated_by.last_name} on{" "}
              {format(
                parseISO(initialCustomResponse.updated_at),
                FORMATS.FRONTEND.DATETIME
              )}
            </Text>
          )}
        </div>
        <Stack spacing={1} direction="row">
          {isNotUndefined(initialCustomResponse) && (
            <>
              <Tooltip title="Copy to clipboard">
                <IconButton onClick={handleCopyToClipboard}>
                  <ContentCopyOutlinedIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete answer">
                <IconButton onClick={handleDelete}>
                  <DeleteOutlineOutlinedIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
          <Button color="secondary" onClick={handleSubmitCustomResponse}>
            Save my answer
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};
export default CustomResponse;
